import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { ArrowRight, X, ChevronDown, Check, Building2, Plus } from "lucide-react";

// ============================================================================
// FINE & COUNTRY BRANCHES DATA
// ============================================================================

const FINE_AND_COUNTRY_BRANCHES = [
"Abingdon and Wallingford",
"Altrincham and Hale",
"Andover and Petersfield",
"Ascot and Wentworth",
"Ashbourne and Matlock",
"Banbury",
"Barnes",
"Barnet",
"Barnstaple",
"Basingstoke and Hook",
"Bath",
"Battersea and Clapham",
"Bawtry",
"Beccles & Waveney",
"Bedford",
"Berkhamsted",
"Biddenham and Ampthill",
"Bideford",
"Birmingham",
"Bolton",
"Bournemouth and East Dorset",
"Braunton",
"Brighton and Hove",
"Bristol and North Somerset",
"Buckingham and Brackley",
"Bury St Edmunds",
"Calder Valley",
"Cambridge and Newmarket",
"Cardiff and Newport",
"Central Lincolnshire and Grantham",
"Cheam and Epsom",
"Cheltenham",
"Chester",
"Chichester",
"Chiswick",
"Christchurch",
"Cobham",
"Colchester",
"Cotswolds and Cirencester",
"Coventry",
"Cumbria",
"Disley and Marple",
"Droitwich Spa",
"Durham and North Yorkshire",
"East Yorkshire",
"Edgbaston and Harborne",
"Edinburgh",
"Emsworth",
"Exeter",
"Fakenham",
"Farnham",
"Fine & Country Redditch and Henley-in-Arden",
"Forest of Dean",
"Formby & South Lancashire",
"Fulham",
"Gloucester and Tewkesbury",
"Godalming and Haslemere",
"Guildford",
"Harpenden and Wheathampstead",
"Harrow Northwood & Pinner",
"Henley-on-Thames",
"Hertford and Welwyn",
"High Peak",
"Holland Park",
"Ilfracombe",
"Isle of Wight",
"Islington",
"Kingswood",
"Knowle and Dorridge",
"Knutsford",
"Lancaster",
"Launceston",
"Leamington Spa",
"Leeds",
"Leicester",
"Loughborough",
"Loughton",
"Lutterworth and Hinckley",
"Lytham",
"Maidstone",
"Malling and The Weald",
"Manchester",
"Market Harborough",
"Marlborough and Devizes",
"Marlow and Maidenhead",
"Merseyside and North Cheshire",
"Mid Cornwall",
"Mid West Cornwall",
"Mid and South Essex",
"Milton Keynes & North Buckinghamshire",
"Minehead and Exmoor",
"Monmouthshire",
"Nantwich and Tarporley",
"Newcastle and Northumberland",
"North Oxfordshire",
"North Wales",
"North West Kent",
"Northampton",
"Northern Lincolnshire \u2013 Barton upon Humber",
"Northern Lincolnshire \u2013 Brigg",
"Northern Lincolnshire \u2013 Gainsborough",
"Northern Lincolnshire \u2013 Grimsby",
"Northern Lincolnshire \u2013 Scunthorpe",
"Norwich",
"Nottingham",
"Nuneaton and Balsall Common",
"Olney and Newport Pagnell",
"Oxford",
"Plymouth and Salcombe",
"Poynton",
"Prestbury and Alderley Edge",
"Putney",
"Reading and Twyford",
"Redbourn and Edlesborough",
"Reigate and East Grinstead",
"Ribble Valley",
"Richmond",
"Rugby",
"Rutland",
"Scotland",
"Shrewsbury and Bridgnorth",
"Solihull",
"South Buckinghamshire",
"South Derbyshire",
"South Devon",
"South Dorset and Sandbanks",
"South East Hampshire",
"South East London",
"South Gloucestershire",
"South Herefordshire",
"South Molton",
"South Norfolk and North Suffolk",
"Southampton and Romsey",
"St Albans",
"St. Neots",
"Staffordshire",
"Stamford & South Lincolnshire",
"Stourbridge and Kidderminster",
"Stratford-upon-Avon",
"Surrey Hills",
"Sutton Coldfield and Lichfield",
"Swansea",
"Taunton",
"Teddington and Hampton",
"Telford",
"Tetbury",
"Torrington",
"Totteridge & Hadley Wood",
"Twickenham",
"Wakefield",
"Walton-On-Thames and Chertsey",
"Ware",
"Warlingham",
"Warwick & Kenilworth",
"Wellingborough and Towcester",
"Wellington",
"West Hampstead",
"West Kensington",
"West Lancs and Wigan",
"West Wales",
"Wetherby",
"Weybridge",
"Wilmslow",
"Wimbledon",
"Winchester",
"Windermere",
"Windsor and Englefield Green",
"Wirral",
"Wiveliscombe",
"Woking and Cranleigh",
"Wolverhampton and Walsall",
"Worcester and Malvern",
"York"];


// ============================================================================
// PURE THREE.JS BEAMS IMPLEMENTATION (No R3F - avoids wrapper conflicts)
// ============================================================================

const noise = `
float random (in vec2 st) {
    return fract(sin(dot(st.xy,
                         vec2(12.9898,78.233)))*
        43758.5453123);
}

float noise (in vec2 st) {
    vec2 i = floor(st);
    vec2 f = fract(st);

    float a = random(i);
    float b = random(i + vec2(1.0, 0.0));
    float c = random(i + vec2(0.0, 1.0));
    float d = random(i + vec2(1.0, 1.0));

    vec2 u = f * f * (3.0 - 2.0 * f);

    return mix(a, b, u.x) +
           (c - a)* u.y * (1.0 - u.x) +
           (d - b) * u.x * u.y;
}

vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

vec3 fade(vec3 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}

float cnoise(vec3 P){
  vec3 Pi0 = floor(P);
  vec3 Pi1 = Pi0 + vec3(1.0);
  Pi0 = mod(Pi0, 289.0);
  Pi1 = mod(Pi1, 289.0);
  vec3 Pf0 = fract(P);
  vec3 Pf1 = Pf0 - vec3(1.0);
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 / 7.0;
  vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 / 7.0;
  vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000,g000),dot(g010,g010),dot(g100,g100),dot(g110,g110)));
  g000 *= norm0.x; g010 *= norm0.y; g100 *= norm0.z; g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001,g001),dot(g011,g011),dot(g101,g101),dot(g111,g111)));
  g001 *= norm1.x; g011 *= norm1.y; g101 *= norm1.z; g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x,Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x,Pf1.y,Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy,Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy,Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x,Pf0.y,Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x,Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000,n100,n010,n110),vec4(n001,n101,n011,n111),fade_xyz.z);
  vec2 n_yz = mix(n_z.xy,n_z.zw,fade_xyz.y);
  float n_xyz = mix(n_yz.x,n_yz.y,fade_xyz.x);
  return 2.2 * n_xyz;
}
`;

function createStackedPlanesBufferGeometry(n, width, height, spacing, heightSegments) {
  const geometry = new THREE.BufferGeometry();
  const numVertices = n * (heightSegments + 1) * 2;
  const numFaces = n * heightSegments * 2;
  const positions = new Float32Array(numVertices * 3);
  const indices = new Uint32Array(numFaces * 3);
  const uvs = new Float32Array(numVertices * 2);
  let vertexOffset = 0,indexOffset = 0,uvOffset = 0;
  const totalWidth = n * width + (n - 1) * spacing;
  const xOffsetBase = -totalWidth / 2;

  for (let i = 0; i < n; i++) {
    const xOffset = xOffsetBase + i * (width + spacing);
    const uvXOffset = Math.random() * 300;
    const uvYOffset = Math.random() * 300;

    for (let j = 0; j <= heightSegments; j++) {
      const y = height * (j / heightSegments - 0.5);
      positions.set([xOffset, y, 0, xOffset + width, y, 0], vertexOffset * 3);
      const uvY = j / heightSegments;
      uvs.set([uvXOffset, uvY + uvYOffset, uvXOffset + 1, uvY + uvYOffset], uvOffset);

      if (j < heightSegments) {
        const a = vertexOffset,b = vertexOffset + 1,c = vertexOffset + 2,d = vertexOffset + 3;
        indices.set([a, b, c, c, b, d], indexOffset);
        indexOffset += 6;
      }
      vertexOffset += 2;
      uvOffset += 4;
    }
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  return geometry;
}

function createBeamMaterial(speed, noiseIntensity, scale) {
  const physical = THREE.ShaderLib.physical;
  const { vertexShader: baseVert, fragmentShader: baseFrag, uniforms: baseUniforms } = physical;
  const baseDefines = physical.defines ?? {};

  const uniforms = THREE.UniformsUtils.clone(baseUniforms);
  uniforms.diffuse = { value: new THREE.Color(0, 0, 0) };
  uniforms.time = { value: 0 };
  uniforms.roughness = { value: 0.3 };
  uniforms.metalness = { value: 0.3 };
  uniforms.uSpeed = { value: speed };
  uniforms.envMapIntensity = { value: 10 };
  uniforms.uNoiseIntensity = { value: noiseIntensity };
  uniforms.uScale = { value: scale };

  const header = `
    varying vec3 vEye;
    varying float vNoise;
    varying vec2 vUv;
    varying vec3 vPosition;
    uniform float time;
    uniform float uSpeed;
    uniform float uNoiseIntensity;
    uniform float uScale;
    ${noise}
  `;

  const vertexHeader = `
    float getPos(vec3 pos) {
      vec3 noisePos = vec3(pos.x * 0., pos.y - uv.y, pos.z + time * uSpeed * 3.) * uScale;
      return cnoise(noisePos);
    }
    vec3 getCurrentPos(vec3 pos) {
      vec3 newpos = pos;
      newpos.z += getPos(pos);
      return newpos;
    }
    vec3 getNormal(vec3 pos) {
      vec3 curpos = getCurrentPos(pos);
      vec3 nextposX = getCurrentPos(pos + vec3(0.01, 0.0, 0.0));
      vec3 nextposZ = getCurrentPos(pos + vec3(0.0, -0.01, 0.0));
      vec3 tangentX = normalize(nextposX - curpos);
      vec3 tangentZ = normalize(nextposZ - curpos);
      return normalize(cross(tangentZ, tangentX));
    }
  `;

  let vert = `${header}\n${vertexHeader}\n${baseVert}`;
  let frag = `${header}\n${baseFrag}`;

  vert = vert.replace("#include <begin_vertex>", "#include <begin_vertex>\ntransformed.z += getPos(transformed.xyz);");
  vert = vert.replace("#include <beginnormal_vertex>", "#include <beginnormal_vertex>\nobjectNormal = getNormal(position.xyz);");
  frag = frag.replace("#include <dithering_fragment>", `#include <dithering_fragment>
    float randomNoise = noise(gl_FragCoord.xy);
    gl_FragColor.rgb -= randomNoise / 15. * uNoiseIntensity;
  `);

  return new THREE.ShaderMaterial({
    defines: { ...baseDefines },
    uniforms,
    vertexShader: vert,
    fragmentShader: frag,
    lights: true,
    fog: true
  });
}

// Pure Three.js Beams Component
const PureThreeBeams = ({ beamWidth = 2.5, beamHeight = 18, beamNumber = 15, lightColor = "#ffffff", speed = 2.5, noiseIntensity = 2, scale = 0.15, rotation = 43 }) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const materialRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Setup
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(30, width / height, 0.1, 1000);
    camera.position.set(0, 0, 20);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(lightColor, 1);
    dirLight.position.set(0, 3, 10);
    scene.add(dirLight);

    // Geometry & Material
    const geometry = createStackedPlanesBufferGeometry(beamNumber, beamWidth, beamHeight, 0, 100);
    const material = createBeamMaterial(speed, noiseIntensity, scale);
    materialRef.current = material;

    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.z = THREE.MathUtils.degToRad(rotation);
    scene.add(mesh);

    // Animation
    const clock = new THREE.Clock();
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (materialRef.current && materialRef.current.uniforms) {
        materialRef.current.uniforms.time.value += 0.1 * delta;
      }
      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [beamWidth, beamHeight, beamNumber, lightColor, speed, noiseIntensity, scale, rotation]);

  return <div ref={containerRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />;
};

// ============================================================================
// BRANCH SELECTOR COMPONENT
// ============================================================================

const BranchSelector = ({ selectedBranches, onSelect, onRemove }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customBranch, setCustomBranch] = useState("");
  const dropdownRef = useRef(null);
  const customInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCustomMode(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isCustomMode && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [isCustomMode]);

  const filteredBranches = FINE_AND_COUNTRY_BRANCHES.filter(
    (branch) =>
    branch.toLowerCase().includes(search.toLowerCase()) &&
    !selectedBranches.includes(branch)
  );

  const submitCustomBranch = () => {
    const trimmed = customBranch.trim();
    if (!trimmed) return;
    if (!selectedBranches.includes(trimmed)) {
      onSelect(trimmed);
    }
    setCustomBranch("");
    setIsCustomMode(false);
    setSearch("");
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-full min-h-[56px] px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white cursor-pointer flex items-center justify-between gap-2 hover:border-white/30 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="branch-selector">

        <div className="flex-1 flex flex-wrap gap-2">
          {selectedBranches.length === 0 ?
          <span className="text-white/50 font-manrope">Select your branch(es)...</span> :

          selectedBranches.map((branch) =>
          <span
            key={branch}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-[#FFDE59]/20 to-[#FF914D]/20 border border-[#FF914D]/30 text-sm text-white font-manrope">

                {branch}
                <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(branch);
              }}
              className="ml-1 hover:text-[#FF914D] transition-colors">

                  <X className="h-3 w-3" />
                </button>
              </span>
          )
          }
        </div>
        <ChevronDown className={`h-5 w-5 text-white/50 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </div>

      {isOpen &&
      <div className="absolute top-full left-0 right-0 mt-2 bg-black/95 border border-white/20 rounded-xl shadow-2xl z-50 max-h-80 overflow-hidden flex flex-col">
          {!isCustomMode &&
          <div className="p-3 border-b border-white/10">
              <input
              type="text"
              placeholder="Search branches..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-[#FF914D]/50"
              onClick={(e) => e.stopPropagation()}
              data-testid="branch-search" />

            </div>
          }

          {!isCustomMode &&
          <div className="overflow-y-auto flex-1">
              {filteredBranches.length === 0 ?
            <div className="px-4 py-3 text-white/50 text-center font-manrope">No branches found</div> :

            filteredBranches.map((branch) =>
            <div
              key={branch}
              className="px-4 py-3 hover:bg-white/10 cursor-pointer text-white/80 hover:text-white transition-colors font-manrope flex items-center gap-2"
              onClick={() => {
                onSelect(branch);
                setSearch("");
                setIsOpen(false);
              }}
              data-testid={`branch-option-${branch.toLowerCase().replace(/\s+/g, '-')}`}>

                    <Building2 className="h-4 w-4 text-[#FF914D]" />
                    {branch}
                  </div>
            )
            }
            </div>
          }

          {/* Custom branch input mode */}
          {isCustomMode &&
          <div className="p-4 border-b border-white/10" onClick={(e) => e.stopPropagation()}>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-white/50 font-manrope mb-2">
                Enter your branch name
              </label>
              <input
              ref={customInputRef}
              type="text"
              placeholder="e.g. My Branch Name"
              value={customBranch}
              onChange={(e) => setCustomBranch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitCustomBranch();
                }
                if (e.key === "Escape") {
                  setIsCustomMode(false);
                  setCustomBranch("");
                }
              }}
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/15 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-[#FF914D]/50"
              data-testid="branch-custom-input" />

              <div className="flex items-center gap-2 mt-3">
                <button
                type="button"
                onClick={submitCustomBranch}
                disabled={!customBranch.trim()}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-[#FFDE59] to-[#FF914D] text-black hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed font-manrope"
                data-testid="branch-custom-add">
                  Add branch
                </button>
                <button
                type="button"
                onClick={() => {
                  setIsCustomMode(false);
                  setCustomBranch("");
                }}
                className="px-4 py-2 rounded-full text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors font-manrope"
                data-testid="branch-custom-cancel">
                  Cancel
                </button>
              </div>
            </div>
          }

          {/* Sticky "Other" footer trigger */}
          {!isCustomMode &&
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsCustomMode(true);
            }}
            className="border-t border-white/10 px-4 py-3 text-left font-manrope text-[13px] text-[#FFDE59] hover:bg-[#FF914D]/10 transition-colors flex items-center gap-2"
            data-testid="branch-other-trigger">
              <Plus className="h-4 w-4" />
              Other (add your branch name)
            </button>
          }
        </div>
      }
    </div>);

};

// ============================================================================
// HUBSPOT MEETINGS EMBED
// ============================================================================

const HUBSPOT_ELITE_URL = "https://meetings.hubspot.com/bryan-mansell/gazeal-elite?embed=true";
const HUBSPOT_STANDARD_URL = "https://meetings.hubspot.com/bryan-mansell/gazeal?embed=true";

// Backend API base — leads endpoint persists + emails the Gazeal team
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const submitLead = async (payload) => {
  try {
    const res = await fetch(`${API_BASE}/api/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      // Don't block the user flow if the backend fails; log for follow up
      console.warn("Lead submission returned", res.status);
    }
  } catch (err) {
    console.warn("Lead submission failed", err);
  }
};

const HubSpotMeetingsEmbed = ({ formData = {}, meetingUrl = HUBSPOT_ELITE_URL }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Build the prefilled embed URL
    const [firstName, ...rest] = (formData.fullName || "").trim().split(/\s+/);
    const lastName = rest.join(" ");
    const params = new URLSearchParams();
    if (firstName) params.set("firstName", firstName);
    if (lastName) params.set("lastName", lastName);
    if (formData.email) params.set("email", formData.email);
    const prefilledSrc = `${meetingUrl}${params.toString() ? `&${params.toString()}` : ""}`;

    // Create the embed container div as HubSpot's script expects
    const embed = document.createElement("div");
    embed.className = "meetings-iframe-container";
    embed.setAttribute("data-src", prefilledSrc);
    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(embed);

    // Inject HubSpot script (re-inject each mount so it re-scans for the new div)
    const SCRIPT_SRC = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [formData.fullName, formData.email, meetingUrl]);

  return (
    <div
      ref={containerRef}
      className="w-full rounded-xl overflow-hidden bg-white/[0.02] border border-white/10"
      style={{ minHeight: "640px" }}
      data-testid="hubspot-meetings-embed" />);

};

// ============================================================================
// ACCESS REQUEST MODAL
// ============================================================================

const AccessModal = ({ isOpen, onClose, propertyValue, selectedBranches = [] }) => {
  const isFnC = selectedBranches.length > 0;
  const [formData, setFormData] = useState({
    fullName: "",
    agency: isFnC ? "Fine & Country" : "",
    email: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isFnC) setFormData((f) => ({ ...f, agency: "Fine & Country" }));
  }, [isFnC]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    await submitLead({
      full_name: formData.fullName,
      agency: formData.agency,
      email: formData.email,
      property_value: propertyValue || null,
      lead_type: "elite",
      variant: isFnC ? "fine-and-country" : "default",
      branches: selectedBranches || []
    });

    setIsSubmitting(false);
    setIsSuccess(true);
  };

  const handleClose = () => {
    setFormData({ fullName: "", agency: "", email: "" });
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose} />

      
      {/* Modal */}
      <div className={`relative w-full ${isSuccess ? "max-w-3xl" : "max-w-lg"} bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto`}>
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors z-10"
          data-testid="modal-close">

          <X className="h-6 w-6" />
        </button>

        {!isSuccess ? (
        /* Form State */
        <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-[#FFDE59]/20 to-[#FF914D]/20 border border-[#FF914D]/30 mb-4">
                <Check className="h-8 w-8 text-[#FF914D]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-didot italic mb-2">
                <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent">
                  Apply for Elite Access
                </span>
              </h2>
              <p className="text-white/60 font-manrope text-sm">
                Applications are reviewed within 48 hours
              </p>
            </div>

            {/* Qualification Summary */}
            <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-xs text-white/40 uppercase tracking-wider mb-2 font-manrope">Qualification</div>
              <div className="flex items-center justify-between">
                <span className="text-white/80 font-manrope text-sm">Typical listing value</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-[#FFDE59]/10 to-[#FF914D]/10 border border-[#FF914D]/20 text-sm text-white font-manrope font-semibold">
                  <Building2 className="h-3 w-3 text-[#FF914D]" />
                  {formatGBP(propertyValue || ELITE_THRESHOLD)}
                  {(propertyValue || 0) >= PRICE_MAX ? "+" : ""}
                </span>
              </div>
              {selectedBranches.length > 0 &&
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-xs text-white/40 uppercase tracking-wider mb-2 font-manrope">Fine &amp; Country branch{selectedBranches.length > 1 ? "es" : ""}</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="modal-selected-branches">
                    {selectedBranches.map((b) =>
                      <span key={b} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#FFDE59]/10 to-[#FF914D]/10 border border-[#FF914D]/20 text-xs text-white/90 font-manrope">
                        <Building2 className="h-3 w-3 text-[#FF914D]" />
                        {b}
                      </span>
                    )}
                  </div>
                </div>
              }
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-2 font-manrope">Full Name</label>
                <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-[#FF914D]/50 transition-colors"
                placeholder="Enter your full name"
                data-testid="input-full-name" />

              </div>
              <div>
                <label className="block text-sm text-white/60 mb-2 font-manrope">Agency Name</label>
                <input
                type="text"
                required
                value={formData.agency}
                onChange={(e) => setFormData({ ...formData, agency: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-[#FF914D]/50 transition-colors"
                placeholder="Enter your agency name"
                data-testid="input-agency" />

              </div>
              <div>
                <label className="block text-sm text-white/60 mb-2 font-manrope">Work Email</label>
                <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-[#FF914D]/50 transition-colors"
                placeholder="your.name@agency.com"
                data-testid="input-email" />

              </div>
              <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 px-6 py-4 rounded-full bg-gradient-to-r from-[#FFDE59] to-[#FF914D] text-black font-bold font-manrope hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-access-request">

                {isSubmitting ?
              <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Submitting...
                  </> :

              <>
                    Submit Application
                    <ArrowRight className="h-5 w-5" />
                  </>
              }
              </button>
              <p className="text-white/40 text-xs text-center mt-2 font-manrope">
                By applying you agree to Gazeal's terms for premium agent membership.
              </p>
            </form>
          </div>) : (

        /* Success State — HubSpot Meetings Embed */
        <div className="p-6 sm:p-8" data-testid="calendar-success-state">
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-[#FFDE59] to-[#FF914D] mb-3">
                <Check className="h-6 w-6 text-black" />
              </div>
              <h2 className="text-xl sm:text-2xl font-didot italic mb-1">
                <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent">
                  Book your Elite onboarding call
                </span>
              </h2>
              <p className="text-white/60 font-manrope text-sm">
                Thanks {formData.fullName.split(" ")[0] || "there"}. Pick a slot that works for you below.
              </p>
            </div>
            <HubSpotMeetingsEmbed formData={formData} />
            <div className="mt-4 text-center">
              <button
                onClick={handleClose}
                className="text-white/40 hover:text-white/80 text-sm font-manrope transition-colors"
                data-testid="modal-done">
                Close
              </button>
            </div>
          </div>)
        }
      </div>
    </div>);

};

// ============================================================================
// GAZEAL STANDARD MODAL (for listings under £700k)
// ============================================================================

const StandardModal = ({ isOpen, onClose, propertyValue, variant = "default" }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    agency: "",
    email: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    await submitLead({
      full_name: formData.fullName,
      agency: formData.agency,
      email: formData.email,
      property_value: propertyValue || null,
      lead_type: "standard",
      variant,
      branches: []
    });

    setIsSubmitting(false);
    setIsSuccess(true);
  };

  const handleClose = () => {
    setFormData({ fullName: "", agency: "", email: "" });
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="standard-modal">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose} />

      <div className={`relative w-full ${isSuccess ? "max-w-3xl" : "max-w-lg"} bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto`}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors z-10"
          data-testid="standard-modal-close">
          <X className="h-6 w-6" />
        </button>

        {!isSuccess ? (
        <div className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 border border-white/20 mb-4">
                <Check className="h-8 w-8 text-white/80" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-didot italic mb-3 text-white">
                Gazeal Standard
              </h2>
              <p className="text-white/70 font-manrope text-sm sm:text-base max-w-sm mx-auto leading-relaxed">
                Elite is built for listings at £700k and above. For every other sale, Gazeal Standard gives you binding reservation agreements and pays your commission at sale agreed.
              </p>
            </div>

            <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-xs text-white/40 uppercase tracking-wider mb-2 font-manrope">Your listing</div>
              <div className="flex items-center justify-between">
                <span className="text-white/80 font-manrope text-sm">Typical listing value</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-sm text-white font-manrope font-semibold">
                  <Building2 className="h-3 w-3 text-white/60" />
                  {formatGBP(propertyValue || PRICE_MIN)}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-2 font-manrope">Full Name</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-white/50 transition-colors"
                  placeholder="Enter your full name"
                  data-testid="standard-input-full-name" />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-2 font-manrope">Agency Name</label>
                <input
                  type="text"
                  required
                  value={formData.agency}
                  onChange={(e) => setFormData({ ...formData, agency: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-white/50 transition-colors"
                  placeholder="Enter your agency name"
                  data-testid="standard-input-agency" />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-2 font-manrope">Work Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 font-manrope focus:outline-none focus:border-white/50 transition-colors"
                  placeholder="your.name@agency.com"
                  data-testid="standard-input-email" />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 px-6 py-4 rounded-full bg-white text-black font-bold font-manrope hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="standard-submit">

                {isSubmitting ?
                <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Submitting...
                  </> :

                <>
                    Continue to Booking
                    <ArrowRight className="h-5 w-5" />
                  </>
                }
              </button>
              <p className="text-white/40 text-xs text-center mt-2 font-manrope">
                By continuing you agree to Gazeal's terms of service.
              </p>
            </form>
          </div>) : (

        <div className="p-6 sm:p-8" data-testid="standard-calendar-state">
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white mb-3">
                <Check className="h-6 w-6 text-black" />
              </div>
              <h2 className="text-xl sm:text-2xl font-didot italic mb-1 text-white">
                Book your Gazeal Standard call
              </h2>
              <p className="text-white/60 font-manrope text-sm">
                Thanks {formData.fullName.split(" ")[0] || "there"}. Pick a slot that works for you below.
              </p>
            </div>
            <HubSpotMeetingsEmbed formData={formData} meetingUrl={HUBSPOT_STANDARD_URL} />
            <div className="mt-4 text-center">
              <button
                onClick={handleClose}
                className="text-white/40 hover:text-white/80 text-sm font-manrope transition-colors"
                data-testid="standard-modal-done">
                Close
              </button>
            </div>
          </div>)
        }
      </div>
    </div>);

};



// ============================================================================
// BUTTON COMPONENT
// ============================================================================

const Button = ({ variant = "default", size = "sm", className = "", children, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 disabled:pointer-events-none disabled:opacity-50";

  const variants = {
    default: "bg-gradient-to-r from-[#FFDE59] to-[#FF914D] text-black hover:opacity-90",
    outline: "border border-white/20 bg-white/5 backdrop-blur-xl text-white hover:bg-white/10 hover:border-white/30",
    ghost: "text-white/90 hover:text-white hover:bg-white/10"
  };

  const sizes = {
    sm: "h-9 px-4 py-2 text-sm",
    lg: "px-8 py-6 text-lg"
  };

  return (
    <button
      className={`group relative overflow-hidden rounded-full ${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}>

      <span className="relative z-10 flex items-center">{children}</span>
      <div className="absolute inset-0 -top-2 -bottom-2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
    </button>);

};

// ============================================================================
// GAZEAL ELITE HERO COMPONENT
// ============================================================================

// ============================================================================
// PRICE QUALIFIER (Elite £700k+ Self-Qualification Slider)
// ============================================================================

const ELITE_THRESHOLD = 700000;
const PRICE_MIN = 250000;
const PRICE_MAX = 5000000;

const formatGBP = (v) => {
  if (v >= 1000000) {
    const m = v / 1000000;
    return `\xA3${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  return `\xA3${Math.round(v / 1000)}k`;
};

const PriceQualifier = ({ value, onChange }) => {
  const qualified = value >= ELITE_THRESHOLD;
  const progress = ((value - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100;
  const thresholdPct = ((ELITE_THRESHOLD - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100;

  return (
    <div className="w-full" data-testid="price-qualifier">
      {/* Current value display */}
      <div className="flex justify-between items-baseline mb-4 font-manrope">
        <span className="text-white/50 text-xs uppercase tracking-[0.15em]">
          Your typical listing value
        </span>
        <span
          className={`text-2xl sm:text-3xl font-bold transition-all duration-300 ${
            qualified
              ? "bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent"
              : "text-white/60"
          }`}
          data-testid="price-qualifier-value">
          {formatGBP(value)}
          {value >= PRICE_MAX ? "+" : ""}
        </span>
      </div>

      {/* Slider track */}
      <div className="relative py-3">
        {/* Threshold marker line */}
        <div
          className="absolute top-0 bottom-0 w-px bg-white/20 pointer-events-none z-0"
          style={{ left: `${thresholdPct}%` }} />

        <div
          className="absolute -top-1 text-[10px] text-white/50 font-manrope -translate-x-1/2 pointer-events-none z-0 font-medium tracking-wide"
          style={{ left: `${thresholdPct}%` }}>
          £700k
        </div>

        <input
          type="range"
          min={PRICE_MIN}
          max={PRICE_MAX}
          step={25000}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`relative z-10 w-full slider-elite ${qualified ? "qualified" : ""}`}
          style={{ "--progress": `${progress}%` }}
          data-testid="price-qualifier-slider"
          aria-label="Typical listing value" />

      </div>

      {/* Scale markers */}
      <div className="flex justify-between text-[10px] text-white/30 font-manrope mt-3 tracking-wider">
        <span>£250k</span>
        <span>£1M</span>
        <span>£2M</span>
        <span>£3M</span>
        <span>£5M+</span>
      </div>
    </div>);

};

// ============================================================================
// GAZEAL ELITE HERO COMPONENT (MAIN)
// ============================================================================

export default function GazealEliteHero({ variant = "default" }) {
  const isFineAndCountry = variant === "fine-and-country";
  const [propertyValue, setPropertyValue] = useState(900000);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showStandardModal, setShowStandardModal] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);

  const priceQualified = propertyValue >= ELITE_THRESHOLD;
  const branchQualified = isFineAndCountry ? selectedBranches.length > 0 : true;
  const qualified = priceQualified && branchQualified;
  // Show Standard pathway only when the branch requirement is satisfied but price is too low
  const showStandardPath = branchQualified && !priceQualified;

  const stats = [
  { value: "\xA31.2M+", label: "Average Property Value" },
  { value: "95%", label: "Sales Success" },
  { value: "\xA36 Billion +", label: "Property Processed" }];

  return (
    <div className="relative w-full overflow-hidden bg-black">
      {/* 3D Beams Background - Pure Three.js (no R3F) */}
      <div className="absolute inset-0 z-0">
        <PureThreeBeams
          beamWidth={2.5}
          beamHeight={18}
          beamNumber={15}
          lightColor="#ffffff"
          speed={2.5}
          noiseIntensity={2}
          scale={0.15}
          rotation={43} />

      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/50 via-transparent to-black/30 pointer-events-none" />

      {/* Hero Content */}
      <div className="relative z-10 flex items-start pt-32 pb-4 md:pt-40 md:pb-6">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">

            {/* Subtle F&C eyebrow (only on Fine & Country landing) */}
            {isFineAndCountry &&
              <div className="mb-6 flex justify-center" data-testid="fnc-eyebrow">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-xl px-4 py-1.5 text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/75 font-manrope">
                  <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-[#FFDE59] to-[#FF914D]" />
                  Exclusively for Fine &amp; Country agents
                </div>
              </div>
            }

            {/* Main Heading - Optimized for 2 lines with Didot font */}
            <h1 className="mb-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.1] font-didot">
              <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent italic">A private, secure sales service

              </span>
              <br />
              <span className="text-white italic">for premium agents.</span>
            </h1>

            {/* Subtitle */}
            <p className="mb-10 text-lg leading-8 text-white/80 sm:text-xl lg:text-2xl max-w-3xl mx-auto font-manrope" data-testid="hero-subtitle">
              {isFineAndCountry
                ? "Gazeal Elite has been shaped by working closely with Fine & Country Agents using Gazeal. Higher value transactions demand stronger commitment, better protection, and more control. Elite brings all three together in one place."
                : "Gazeal Elite has been shaped by working closely with agents operating at the top end of the market. Higher value transactions demand stronger commitment, better protection, and more control. Elite brings all three together in one place."}
            </p>

            {/* Price Qualifier & CTA */}
            <div id="access-section" className="max-w-xl mx-auto mb-12">
              {/* Glassmorphic qualifier card */}
              <div className={`relative p-6 sm:p-8 rounded-2xl bg-white/[0.04] backdrop-blur-xl border transition-all duration-500 ${qualified ? "border-[#FF914D]/30 shadow-2xl shadow-[#FF914D]/10" : "border-white/10"}`}>

                {/* Branch Selector — only on Fine & Country landing */}
                {isFineAndCountry &&
                  <div className="mb-6 text-left" data-testid="fnc-branch-block">
                    <label className="block text-xs uppercase tracking-[0.18em] text-white/50 font-manrope mb-2">
                      Your Fine &amp; Country Market
                    </label>
                    <BranchSelector
                      selectedBranches={selectedBranches}
                      onSelect={(b) => setSelectedBranches((prev) => [...prev, b])}
                      onRemove={(b) => setSelectedBranches((prev) => prev.filter((x) => x !== b))} />
                  </div>
                }

                <PriceQualifier value={propertyValue} onChange={setPropertyValue} />

                {/* Dynamic micro-copy */}
                <div className="mt-6 min-h-[3rem] flex items-center justify-center" key={qualified ? "q" : showStandardPath ? "std" : "nq"}>
                  <p className={`text-sm sm:text-base font-manrope elite-fade ${qualified ? "text-white/90" : "text-white/60"}`} data-testid="qualifier-status">
                    {qualified ? (
                      <>
                        <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent font-semibold">You qualify for Elite.</span>{" "}
                        Secure your pipeline and unlock commission at sale agreed.
                      </>
                    ) : isFineAndCountry && !branchQualified ? (
                      <>Select your market to continue.</>
                    ) : showStandardPath ? (
                      <>
                        Elite access unlocks at <span className="text-white/80 font-medium">{"\xA3700k"}</span> and above. For the listings below, you can still use Gazeal.
                      </>
                    ) : (
                      <>
                        Elite access unlocks at <span className="text-white/80 font-medium">{"\xA3700k"}</span> and above.
                      </>
                    )}
                  </p>
                </div>

                {/* CTA */}
                {qualified ? (
                  <Button
                    size="lg"
                    className="w-full mt-4 shadow-2xl font-semibold transition-all duration-300 shadow-[#FFDE59]/25"
                    onClick={() => setShowAccessModal(true)}
                    data-testid="hero-cta-button">
                    Apply for Elite Access
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                ) : showStandardPath ? (
                  <button
                    type="button"
                    onClick={() => setShowStandardModal(true)}
                    className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/25 hover:border-white/40 text-white font-semibold font-manrope py-4 px-6 transition-colors"
                    data-testid="hero-standard-cta-button">
                    Book a Gazeal Standard Call
                    <ArrowRight className="h-5 w-5" />
                  </button>
                ) : (
                  <Button
                    size="lg"
                    className="w-full mt-4 opacity-50 cursor-not-allowed grayscale font-semibold"
                    disabled
                    data-testid="hero-cta-button">
                    {isFineAndCountry ? "Select your market" : "Join Waitlist"}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                )}

                {/* Inline Stats (sits inside the qualifier card, under the CTA) */}
                <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-3 gap-2 sm:gap-4" data-testid="hero-stats-section">
                  {stats.map((stat, index) =>
                  <div key={index} className="text-center" data-testid={`stat-card-${index}`}>
                      <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent mb-1 font-outfit">
                        {stat.value}
                      </div>
                      <div className="text-white/50 text-[10px] sm:text-xs font-manrope tracking-wide uppercase leading-tight">{stat.label}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Access Request Modal */}
      <AccessModal
        isOpen={showAccessModal}
        onClose={() => setShowAccessModal(false)}
        propertyValue={propertyValue}
        selectedBranches={isFineAndCountry ? selectedBranches : []} />

      {/* Gazeal Standard Modal */}
      <StandardModal
        isOpen={showStandardModal}
        onClose={() => setShowStandardModal(false)}
        propertyValue={propertyValue}
        variant={isFineAndCountry ? "fine-and-country" : "default"} />

    </div>);

}